<template>
	<div class="content-page content-page--users">
		<div class="content-page__heading" ref="usersContentPageHeading">
			<h2 class="content-page__title">Manage Users</h2>
			<Button
				id="iconOnRight"
				className="btn--has-icon"
				labelText="Add User"
				:btnClick="addUser"
				iconClassName="icon-more"
				:iconBeforeLabel="true"
				:title="
					!addUserDisabled
						? 'You don\'t have permission to perform this action.'
						: 'Select to go to add user.'
				"
				v-auth-permissions="['users.create']"
			/>
		</div>
		<div
			class="results-toolbar results-toolbar--variant-seven"
			ref="usersResultsToolBar"
		>
			<div class="results-toolbar results-toolbar--variant-three">
				<UserFilters
					v-model="filters"
					:defaultFilters="defaultFilters"
					:optionRoles="ddRolesOptions"
					@load-more-roles="loadMoreRoles"
					@search-roles="searchRoles"
					ref="userFiltersRef"
				/>
			</div>

			<div class="results-toolbar__counter">
				<ResultCounter :counter="resultCounter" v-if="hasResults" />
			</div>
		</div>

		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<UsersList :items="usersList" ref="usersList" />
		</ScrollContainer>
		<Spinner v-if="loading || loadingUser" :processor="{ loading: true }" />
		<NoResults v-if="showNoResults" />

		<UserAddModal v-model="showModal" />
		<UserEditFlyout v-model="showFlyout" :user="user" />
	</div>
</template>

<script>
import Spinner from '@/components/visual/spinner/spinner';
import Button from '@/components/forms/button/button';
import ResultCounter from '@/site-components/result-counter/result-counter';
import UserAddModal from '@/sections/users/settings/user-add-modal';
import UserEditFlyout from '@/sections/users/settings/user-edit-flyout';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import UsersList from '@/sections/users/summary/users-list.vue';
import NoResults from '@/site-components/no-results/no-results';
import UserFilters from '@/sections/users/summary/users-filters.vue';
import { removeObjectEmptyValues } from '@/utils/object';
import { getDifferentValues } from '@/utils/function';
import { mapActions, mapGetters } from 'vuex';
import { CheckPermissionsDirective } from '@/directives/vue-authorization/directive.js';
import {
	MODULE_NAME,
	USERS,
	SET_USERS,
	RELOAD_USERS
} from '@/sections/users/users-mutation-types';
import {
	SUPPORT_EXTERNAL,
	SUPPORT_PORTAL
} from '@/sections/users/shared/users-const.js';
import _ from 'lodash';
import { mapState } from 'vuex';

export default {
	name: 'Users',
	model: {
		prop: 'value',
		event: 'change'
	},
	directives: {
		'auth-permissions': CheckPermissionsDirective
	},
	components: {
		Button,
		ResultCounter,
		UserAddModal,
		UserEditFlyout,
		ScrollContainer,
		UsersList,
		Spinner,
		NoResults,
		UserFilters
	},
	props: {
		roleId: {
			type: [Number, String]
		},
		search: {
			type: String
		},
		sortBy: {
			type: String
		},
		sortDirection: {
			type: String
		},
		status: {
			type: String
		},
		count: {
			type: Number
		},
		userId: {
			type: String
		},
		clientId: {
			type: String
		}
	},
	data() {
		return {
			filters: {
				search: this.search,
				sortby: this.sortBy,
				sortdirection: this.sortDirection,
				status: this.status,
				roleId: this.roleId,
				clientId: this.clientId
			},
			defaultFilters: {
				search: '',
				sortby: 'name',
				sortdirection: 'asc',
				status: null,
				roleId: null,
				clientId: null
			},
			page: 1,
			rolePage: 1,
			roleCount: 100,
			roleSearch: '',
			countPerPage: this.count,
			showModal: false,
			showFlyout: false,
			loading: false,
			loadingMore: false,
			user: {
				internalIds: [],
				consultantIds: []
			},
			loadingUser: false
		};
	},
	beforeRouteEnter(to, from, next) {
		if (to.name === 'users-manager') {
			to.meta.breadcrumb = [];
		}
		next();
	},

	computed: {
		...mapGetters(MODULE_NAME, [USERS, RELOAD_USERS]),
		...mapState('options', ['allRoles']),

		usersList() {
			return this[USERS].items || [];
		},
		hasResults() {
			return this.usersList?.length;
		},
		hasMoreItemsToLoad() {
			return _.isEmpty(this[USERS])
				? false
				: this.page < this[USERS].totalPageCount;
		},
		addUserDisabled() {
			return this.validatePermission('users.create');
		},
		refreshLocalStorage() {
			return _.get(this, 'allRoles.items.length', 0) === 0;
		},
		resultCounter() {
			return `${this[USERS].items?.length} of ${this[USERS].totalCount}`;
		},
		hasMoreRolesToLoad() {
			return this.rolePage < (this.allRoles.totalPageCount || 0);
		},
		ddRolesOptions() {
			const roles = _.get(this.allRoles, 'items', []);
			if (!_.isEmpty(roles)) {
				return roles.map(role => {
					return {
						text: role.text,
						value: role.value
					};
				});
			}

			return roles;
		},
		showNoResults() {
			return !this.loading && !this.hasResults;
		}
	},
	methods: {
		...mapActions('users', {
			fetchUsers: SET_USERS,
			fetchUserById: 'getUser'
		}),
		...mapActions('roles', ['loadPermissions']),
		...mapActions('options', ['getRolesOptions']),
		addUser() {
			this.showModal = true;
		},
		async loadMoreRoles() {
			this.rolePage += 1;

			await this.fetchRoles();
		},
		async searchRoles(searchQuery) {
			this.rolePage = 1;
			this.roleSearch = searchQuery;

			await this.fetchRoles();
		},
		async fetchRoles() {
			return new Promise((resolve, reject) => {
				this.getRolesOptions({
					page: this.rolePage,
					count: this.roleCount,
					sortdirection: 'asc',
					search: this.roleSearch
				})
					.then(() => {
						this.$nextTick(() => {
							if (
								this.$refs.userFiltersRef?.$refs
									.rolesDropdownRef
							) {
								this.$refs.userFiltersRef?.$refs.rolesDropdownRef.stopLoading();
							}
							if (
								this.$refs.userFiltersRef?.$refs
									.rolesDropdownModalRef
							) {
								this.$refs.userFiltersRef?.$refs.rolesDropdownModalRef.stopLoading();
							}
						});
						resolve();
					})
					.catch(err => {
						reject(err);
					});
			});
		},
		async fetchData(page) {
			let params = removeObjectEmptyValues(this.filters);

			params.page = page;
			params.count = this.countPerPage || 100;
			if (params.status) {
				const status = (params.status || '').toLowerCase();
				if (status === 'locked') {
					params.locked = true;
				}
				if (status === 'active' || status === 'inactive') {
					params.active = status === 'active';
				}
				if (status === 'corrupted') {
					params.corrupted = true;
				}
			}
			delete params.status;
			delete params.userId;

			if (!this.loadingMore) {
				this.loading = true;
			}

			try {
				if (this.refreshLocalStorage) {
					await Promise.all([
						this.fetchRoles({ count: 1000 }),
						this.fetchUsers(params)
					]).then();
				} else {
					await this.fetchUsers(params);
				}
			} finally {
				this.loading = false;
				this.loadingMore = false;
			}
		},
		updateRoute(queries) {
			let queryParameters = getDifferentValues(
				this.defaultFilters,
				queries
			);

			if (this.showFlyout) {
				let userId = this.user.userId ?? queries.userId;
				if (userId) {
					queryParameters.userId = userId;
				}
			} else {
				delete queryParameters.userId;
			}

			this.$router
				.push({
					path: `/users`,
					query: queryParameters
				})
				.catch(() => {});
		},

		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page += 1;
			}
		},
		async getUserById(userId) {
			const getRolesByClientId = (roles, clientId) => {
				return roles
					.filter(role => role.clientId === clientId)
					.map(item => item.roleId);
			};
			if (userId) {
				this.loadingUser = true;

				await this.fetchUserById(userId)
					.then(resp => {
						const { isSuccessful } = resp;

						if (isSuccessful) {
							let userCopy = _.omit(resp, [
								'roles',
								'isSuccessful'
							]);
							const { roles } = resp;

							if (resp && roles) {
								userCopy.internalIds = getRolesByClientId(
									roles,
									SUPPORT_PORTAL
								);
								userCopy.consultantIds = getRolesByClientId(
									roles,
									SUPPORT_EXTERNAL
								);
							}

							this.user = userCopy;
							this.showFlyout = true;
						}
						if (!isSuccessful) {
							this.updateRoute(this.filters);
						}
					})
					.finally(() => {
						this.loadingUser = false;
					});
			}
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(obj) {
				this.updateRoute(obj);
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(1);
				}
			}
		},
		page: {
			handler(page) {
				this.fetchData(page);
			}
		},
		showFlyout: {
			handler(val) {
				if (val) {
					this.updateRoute({
						...this.filters,
						userId: this.user.userId
					});
				} else {
					this.user = { internalIds: [], consultantIds: [] };
					this.updateRoute(this.filters);
				}
			}
		},
		[RELOAD_USERS]: {
			handler(val) {
				if (val) {
					this.fetchData(1);
				}
			}
		},
		userId: {
			immediate: true,
			async handler(userId) {
				if (userId) {
					if (_.isEmpty(this.usersList)) {
						await this.fetchData(1);
					}
					await this.getUserById(this.userId);
				}
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (!newQuery.userId) {
					let newfilters = { ...this.defaultFilters, ...newQuery };
					if (!_.isEqual(newfilters, this.filters)) {
						if (newfilters.roleId !== null) {
							newfilters.roleId = parseInt(newfilters.roleId);
						}
						this.filters = _.cloneDeep(newfilters);
					}
				}
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		if (to.name === 'user-history') {
			to.meta.breadcrumb = [
				{
					name: 'Users',
					data: { path: from.fullPath }
				},
				{ name: 'History' }
			];
			const obj = { path: to.fullPath, breadcrumb: to.meta.breadcrumb };
			this.$store.commit('setBreadCrumbs', obj);
		}
		next();
	}
};
</script>

<style lang="scss" scoped>
.results-toolbar {
	&--variant-three {
		width: 100%;
	}
}
/**
    * Manage Users Page
*/
</style>
